/**
 * Global modal body-scroll lock/restore for the pharmacy panel.
 * reactstrap + nested SweetAlert2 dialogs can leave <body> locked
 * after close, or fail to re-lock on a subsequent open if a pending restore runs.
 *
 * Avoid observing our own body style/class writes (that caused a browser hang).
 */

const isElementDisplayed = (el) => {
	if (!el) return false;
	if (el.style?.display === "block") return true;
	try {
		return window.getComputedStyle(el).display !== "none";
	} catch {
		return false;
	}
};

const isModalLockElement = (el) =>
	el instanceof Element &&
	(el.classList?.contains("modal") ||
		el.classList?.contains("modal-backdrop") ||
		el.classList?.contains("swal2-container") ||
		Boolean(el.querySelector?.(".modal, .modal-backdrop, .swal2-container")));

export const hasOpenModalScrollLocks = () => {
	if (typeof document === "undefined") return false;
	if (document.querySelectorAll(".modal.show").length > 0) return true;
	if (document.querySelectorAll(".modal-backdrop").length > 0) return true;
	if (document.querySelector(".swal2-container")) return true;

	const modals = document.querySelectorAll(".modal");
	for (let i = 0; i < modals.length; i += 1) {
		if (isElementDisplayed(modals[i])) return true;
	}
	return false;
};

export const ensureBodyScrollLocked = () => {
	if (typeof document === "undefined") return;
	if (!document.body.classList.contains("modal-open")) {
		document.body.classList.add("modal-open");
	}
	if (document.body.style.overflow !== "hidden") {
		document.body.style.overflow = "hidden";
	}
	if (document.documentElement.style.overflow !== "hidden") {
		document.documentElement.style.overflow = "hidden";
	}
};

export const restoreBodyScrollIfNoModals = () => {
	if (typeof document === "undefined") return;
	if (hasOpenModalScrollLocks()) {
		ensureBodyScrollLocked();
		return;
	}

	document.body.classList.remove("modal-open", "swal2-shown", "swal2-height-auto");
	document.body.style.overflow = "";
	document.body.style.paddingRight = "";
	document.documentElement.classList.remove("swal2-shown", "swal2-height-auto");
	document.documentElement.style.overflow = "";
	document.documentElement.style.paddingRight = "";
};

const isModalRelatedMutation = (mutation) => {
	const target = mutation.target;
	if (!(target instanceof Element)) {
		if (mutation.type === "childList") {
			const nodes = [
				...Array.from(mutation.addedNodes),
				...Array.from(mutation.removedNodes),
			];
			return nodes.some(isModalLockElement);
		}
		return false;
	}

	if (
		target.classList?.contains("modal") ||
		target.classList?.contains("modal-backdrop") ||
		target.classList?.contains("swal2-container")
	) {
		return true;
	}

	if (mutation.type === "childList") {
		const nodes = [
			...Array.from(mutation.addedNodes),
			...Array.from(mutation.removedNodes),
		];
		return nodes.some(isModalLockElement);
	}

	return false;
};

/**
 * Watch DOM for modal open/close (any close path).
 * Idempotent: safe to call once from App.
 * @returns {() => void} uninstall
 */
export const installGlobalModalScrollRestore = () => {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return () => {};
	}
	if (window.__clinicdotsModalScrollRestoreInstalled) {
		return () => {};
	}
	window.__clinicdotsModalScrollRestoreInstalled = true;

	let timer = null;
	let syncing = false;
	let lastHadOpen = hasOpenModalScrollLocks();

	const syncScrollLock = () => {
		if (syncing) return;
		syncing = true;
		try {
			const hasOpen = hasOpenModalScrollLocks();

			if (hasOpen) {
				if (timer != null) {
					window.clearTimeout(timer);
					timer = null;
				}
				ensureBodyScrollLocked();
				lastHadOpen = true;
				return;
			}

			if (lastHadOpen || document.body.classList.contains("modal-open")) {
				if (timer != null) window.clearTimeout(timer);
				timer = window.setTimeout(() => {
					timer = null;
					syncing = true;
					try {
						restoreBodyScrollIfNoModals();
						lastHadOpen = hasOpenModalScrollLocks();
					} finally {
						syncing = false;
					}
				}, 300);
			}
			lastHadOpen = false;
		} finally {
			syncing = false;
		}
	};

	const observer = new MutationObserver((mutations) => {
		if (syncing) return;
		if (!mutations.some(isModalRelatedMutation)) return;
		syncScrollLock();
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ["class"],
	});

	syncScrollLock();

	return () => {
		if (timer != null) window.clearTimeout(timer);
		observer.disconnect();
		window.__clinicdotsModalScrollRestoreInstalled = false;
	};
};

const modalScrollRestore = {
	hasOpenModalScrollLocks,
	ensureBodyScrollLocked,
	restoreBodyScrollIfNoModals,
	installGlobalModalScrollRestore,
};

export default modalScrollRestore;
